import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DndContext, PointerSensor, useDraggable, useSensor, useSensors } from "@dnd-kit/core";
import { AiOutlineSetting } from "react-icons/ai";
import { MdHome } from "react-icons/md";
import {
  TbChartAreaLine,
  TbMailPlus,
  TbShieldCheck,
  TbUsers,
  TbUser,
  TbBuilding,
  TbSpeakerphone,
  TbFingerprint,
  TbPlugConnected,
  TbServer2,
  TbCloudDollar,
  TbKey,
  TbEye,
  TbEyeSearch,
  TbHistory,
  TbChartBar,
  TbReportAnalytics,
  TbTrendingUp,
  TbTags,
} from "react-icons/tb";
import Title from "../common/Title";
import DesktopWindow from "../product/DesktopWindow";
import InvitationsGrid from "../invitations/InvitationsGrid";
import PermissionsManager from "../permissions/PermissionsManager";
import UsersGrid from "../user/UsersGrid";
import UserForm from "../user/UserForm";
import ClientGrid from "../client/ClientGrid";
import ClientForm from "../client/ClientForm";
import AuthenticatorsGrid from "../client/AuthenticatorGrid";
import AuthenticatorForm from "../client/AuthenticatorForm";
import BroadcastMessageGrid from "../broadcastMessage/BroadcastMessageGrid";
import BroadcastMessageForm from "../broadcastMessage/BroadcastMessageForm";
import BroadcastMessageReviewGrid from "../broadcastMessage/BroadcastMessageReviewGrid";
import ArmaninoSSO from "../product/ArmaninoSSO";
import RunbookSystemGrid from "../runbookSystem/RunbookSystemGrid";
import RunbookTagGrid from "../runbook/RunbookTagGrid";
import RunbookSystemForm from "../runbookSystem/RunbookSystemForm";
import RunbookServerGrid from "../runbookServer/RunbookServerGrid";
import RunbookServerForm from "../runbookServer/RunbookServerForm";
import McpTokenGrid from "../mcpToken/McpTokenGrid";
import InspectRequestGrid from "../inspectRequest/InspectRequestGrid";
import AzureCostGrid from "../azureCost/AzureCostGrid";
import UsageAnalyticsOverview from "../usageAnalytics/product/UsageAnalyticsOverview";
import AuditLogGrid from "../auditlog/AuditLogGrid";
import Analytics from "../analytics/Analytics";
import AllClientsAnalytics from "../analytics/superuser/AllClientsAnalytics";
import UsageAnalytics from "../usageAnalytics/UsageAnalytics";
import { useDarkMode } from "../../contexts/DarkModeContext";
import { useDesktopWindows, WINDOW_START_Y } from "../../hooks/useDesktopWindows";
import { useDesktopIconPositions } from "../../hooks/useDesktopIconPositions";
import armaninoLogo from "../../assets/armanino-llp.png";
import armaninoLogoDark from "../../assets/Armanino-logo-reversed.png";
import "./SettingsHub.css";

// `role` gates which icons render for a given user (see isIconVisible): Product Team
// members see everything in this hub; Super Users see superUser/adminOrSuper areas
// (Clients/Messages and Users/Invitations/Permissions) but not the Product Team-only
// tooling below unless they're also flagged Product Team; plain Admins see only the
// adminOrSuper areas.
const ICONS = [
  {
    id: "invitations",
    label: "Invitations",
    to: "/invitation",
    Icon: TbMailPlus,
    opensWindow: true,
    color: "#b36d00",
    role: "adminOrSuper",
  },
  {
    id: "permissions",
    label: "Permissions",
    to: "/permissions",
    Icon: TbShieldCheck,
    opensWindow: true,
    color: "#8a8800",
    role: "adminOrSuper",
    maximize: true,
  },
  {
    id: "users",
    label: "Users",
    to: "/user",
    Icon: TbUsers,
    opensWindow: true,
    color: "#e69500",
    role: "adminOrSuper",
    width: 980,
    height: 640,
  },
  {
    id: "runbook-tags",
    label: "Runbook Groups",
    to: "/runbooktag",
    Icon: TbTags,
    opensWindow: true,
    color: "#b36d00",
    role: "adminOrSuper",
  },
  {
    id: "clients",
    label: "Clients",
    to: "/client",
    Icon: TbBuilding,
    opensWindow: true,
    color: "#cf7f00",
    role: "superUser",
  },
  {
    id: "messages",
    label: "Messages",
    to: "/messages",
    Icon: TbSpeakerphone,
    opensWindow: true,
    color: "#b36d00",
    role: "superUser",
    width: 860,
  },
  {
    id: "armanino-sso",
    label: "Armanino SSO",
    to: "/armanino-sso",
    Icon: TbFingerprint,
    opensWindow: true,
    color: "#cf7f00",
    role: "productTeam",
  },
  {
    id: "systems",
    label: "Runbook Systems",
    to: "/product/systems",
    Icon: TbPlugConnected,
    opensWindow: true,
    color: "#949300",
    role: "productTeam",
  },
  {
    id: "servers",
    label: "Runbook Servers",
    to: "/product/servers",
    Icon: TbServer2,
    opensWindow: true,
    color: "#b36d00",
    role: "productTeam",
  },
  {
    id: "azure-costs",
    label: "Azure Costs",
    to: "/product/azure-costs",
    Icon: TbCloudDollar,
    opensWindow: true,
    color: "#8a8800",
    role: "productTeam",
    maximize: true,
  },
  {
    id: "mcp-tokens",
    label: "MCP Tokens",
    to: "/product/mcp-tokens",
    Icon: TbKey,
    opensWindow: true,
    color: "#e69500",
    role: "productTeam",
    width: 980,
    height: 700,
  },
  {
    id: "usage-analytics",
    label: "Usage Analytics",
    to: "/product/usage-analytics",
    Icon: TbChartAreaLine,
    opensWindow: true,
    color: "#a5a300",
    role: "productTeam",
    fill: true,
  },
  {
    id: "inspect-requests",
    label: "Inspect Requests",
    to: "/product/inspect-requests",
    Icon: TbEyeSearch,
    opensWindow: true,
    color: "#cf7f00",
    // "superUser" role = visible to Product Team (via isIconVisible's productTeam
    // short-circuit) OR Super User, matching the /product/inspect-requests route's
    // anyOf={["superUser","productTeam"]} gate - not Admin alone.
    role: "superUser",
    width: 980,
    height: 700,
  },
  {
    id: "auditlog",
    label: "Audit Log",
    to: "/auditlog",
    Icon: TbHistory,
    opensWindow: true,
    color: "#8a8800",
    role: "adminOrSuper",
    maximize: true,
  },
  {
    id: "my-analytics",
    label: "My Analytics",
    to: "/analytics",
    Icon: TbChartBar,
    opensWindow: true,
    color: "#e69500",
    role: "adminOrSuper",
    fill: true,
  },
  {
    id: "all-clients-analytics",
    label: "All Clients Analytics",
    to: "/analytics/all-clients",
    Icon: TbReportAnalytics,
    opensWindow: true,
    color: "#cf7f00",
    role: "superUser",
    fill: true,
  },
  {
    id: "my-usage",
    label: "My Usage",
    to: "/usage",
    Icon: TbTrendingUp,
    opensWindow: true,
    color: "#b36d00",
    role: "adminOrSuper",
    fill: true,
  },
].sort((a, b) => a.label.localeCompare(b.label));

function getIconColor(id) {
  return ICONS.find((i) => i.id === id)?.color;
}

const USER_FORM_WINDOW_ID = "user-form";
const CLIENT_FORM_WINDOW_ID = "client-form";
const CLIENT_AUTHENTICATORS_WINDOW_ID = "client-authenticators";
const CLIENT_AUTHENTICATOR_FORM_WINDOW_ID = "client-authenticator-form";
const MESSAGE_FORM_WINDOW_ID = "message-form";
const MESSAGE_REVIEWS_WINDOW_ID = "message-reviews";
const SSO_AUTHENTICATOR_FORM_WINDOW_ID = "sso-authenticator-form";
const SYSTEM_FORM_WINDOW_ID = "system-form";
const SERVER_FORM_WINDOW_ID = "server-form";

const WINDOW_Z_BASE = 500;
// The sticky app header sits above windows in z-index, so a window dragged past this
// point has its title bar hidden underneath it and becomes unreachable.
const WINDOW_MIN_Y = 65;

const STORAGE_KEY = "settingsHubIconPositions";

// Product Team sees every icon in this hub; Super User/Admin are otherwise scoped to
// their own role bucket (see the ICONS comment above).
function isIconVisible(icon, user) {
  if (user?.isProductTeam) return true;
  if (icon.role === "superUser") return !!user?.isSuperUser;
  if (icon.role === "productTeam") return false;
  return !!(user?.isAdmin || user?.isSuperUser);
}

function DesktopIcon({ icon, position, isSelected, onSelect, onOpen }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: icon.id });
  const { Icon } = icon;

  const style = {
    transform: `translate3d(${position.x + (transform?.x ?? 0)}px, ${position.y + (transform?.y ?? 0)}px, 0)`,
    zIndex: isDragging ? 10 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={style}
      className={`settings-desktop-icon${isSelected ? " selected" : ""}${isDragging ? " dragging" : ""}`}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(icon.id);
      }}
      onDoubleClick={() => onOpen(icon)}
      onKeyDown={(e) => {
        if (e.key === "Enter") onOpen(icon);
      }}
      aria-label={icon.label}
    >
      <div className="settings-desktop-icon-glyph" style={{ "--tile-color": icon.color }}>
        <Icon />
      </div>
      <span className="settings-desktop-icon-label">{icon.label}</span>
    </div>
  );
}

export default function SettingsHub({ user, hideTitle }) {
  const navigate = useNavigate();
  const { darkMode } = useDarkMode();
  const containerRef = useRef(null);
  const watermarkRef = useRef(null);
  const [selectedId, setSelectedId] = useState(null);
  // Bumped whenever the Client form window (Add/Renew) saves successfully, so the separate
  // Clients grid window — a sibling, not a parent/child — knows to refetch. Closing the form
  // window doesn't change the grid's own `location`, which is otherwise all it refetches on.
  const [clientListRefreshSignal, setClientListRefreshSignal] = useState(0);
  // Same pattern for the Users grid window and its sibling User form window (#2391).
  const [userListRefreshSignal, setUserListRefreshSignal] = useState(0);

  const visibleIcons = ICONS.filter((icon) => isIconVisible(icon, user));

  const {
    openWindows,
    openWindow,
    openOrUpdateWindow,
    closeWindow,
    focusWindow,
    minimizeWindow,
    toggleMaximizeWindow,
    handleWindowResize,
    handleWindowDragEnd,
  } = useDesktopWindows({ minY: WINDOW_MIN_Y });

  // Pops the window's route open in its own browser window (reusing the same session
  // cookies) and closes the floating copy, since the two would otherwise show duplicate
  // live state for the same feature.
  const handlePopOutWindow = (win) => {
    if (!win.to) return;
    window.open(win.to, `popout-${win.id}`, `width=${win.width},height=${win.height},left=${win.x},top=${win.y}`);
    closeWindow(win.id);
  };
  // Not `centered`: with 15 icons, SettingsHub's grid already fills most of the canvas, so
  // the classic top-left corner anchor (which Workspace/ProductHub moved away from — see
  // useDesktopIconPositions) still reads as intentional here rather than stranded.
  const { positions, handleDragEnd, handleReset } = useDesktopIconPositions({
    icons: visibleIcons,
    storageKey: STORAGE_KEY,
    containerRef,
    centered: false,
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
  );
  const windowSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
  );

  const openUserFormWindow = (userId) => {
    openOrUpdateWindow(USER_FORM_WINDOW_ID, {
      title: "Edit User",
      Icon: TbUser,
      color: getIconColor("users"),
      meta: { userId },
    });
  };

  const openClientFormWindow = ({ mode, clientId }) => {
    const title = mode === "renew" ? "Renew Client" : "Add Client";
    openOrUpdateWindow(CLIENT_FORM_WINDOW_ID, {
      title,
      Icon: TbBuilding,
      color: getIconColor("clients"),
      meta: { mode, clientId },
    });
  };

  const openClientAuthenticatorsWindow = (clientId) => {
    openOrUpdateWindow(CLIENT_AUTHENTICATORS_WINDOW_ID, {
      title: "Authenticators",
      Icon: TbKey,
      color: getIconColor("clients"),
      meta: { clientId },
    });
  };

  const openClientAuthenticatorFormWindow = ({ mode, authId, clientId }) => {
    const title = mode === "edit" ? "Edit Authenticator" : "Add Authenticator";
    openOrUpdateWindow(CLIENT_AUTHENTICATOR_FORM_WINDOW_ID, {
      title,
      Icon: TbKey,
      color: getIconColor("clients"),
      meta: { mode, authId, clientId },
    });
  };

  const openMessageFormWindow = (messageId) => {
    openOrUpdateWindow(MESSAGE_FORM_WINDOW_ID, {
      title: messageId ? "Edit Broadcast Message" : "Add Broadcast Message",
      Icon: TbSpeakerphone,
      color: getIconColor("messages"),
      meta: { messageId },
    });
  };

  const openMessageReviewsWindow = (messageId) => {
    openOrUpdateWindow(MESSAGE_REVIEWS_WINDOW_ID, {
      title: "Reviews",
      Icon: TbEye,
      color: getIconColor("messages"),
      meta: { messageId },
    });
  };

  const openSsoAuthenticatorWindow = ({ mode, authId, clientId }) => {
    const title = mode === "edit" ? "Edit Authenticator" : "Add Authenticator";
    openOrUpdateWindow(SSO_AUTHENTICATOR_FORM_WINDOW_ID, {
      title,
      Icon: TbFingerprint,
      color: getIconColor("armanino-sso"),
      meta: { mode, authId, clientId },
    });
  };

  const openSystemFormWindow = ({ mode, system }) => {
    const title =
      mode === "edit" ? "Edit Runbook System" : mode === "view" ? "View Runbook System" : "Add Runbook System";
    openOrUpdateWindow(SYSTEM_FORM_WINDOW_ID, {
      title,
      Icon: TbPlugConnected,
      color: getIconColor("systems"),
      meta: { mode, system },
      // Larger than the default window, and offset further right so it doesn't
      // land directly on top of the Runbook Systems window that opened it.
      sizing: () => {
        const width = Math.min(760, window.innerWidth - 32);
        const height = Math.min(720, window.innerHeight - 96);
        const x = Math.min(780, window.innerWidth - width - 16);
        return { x, y: WINDOW_START_Y, width, height };
      },
    });
  };

  const openServerFormWindow = ({ mode, serverName }) => {
    const title = mode === "edit" ? "Edit Runbook Server" : "Add Runbook Server";
    openOrUpdateWindow(SERVER_FORM_WINDOW_ID, {
      title,
      Icon: TbServer2,
      color: getIconColor("servers"),
      meta: { mode, serverName },
    });
  };

  const renderWindowBody = (win) => {
    if (win.id === "armanino-sso") {
      return (
        <ArmaninoSSO
          user={user}
          hideTitle
          onEditAuthenticator={(authId) => openSsoAuthenticatorWindow({ mode: "edit", authId })}
          onAddAuthenticator={() => openSsoAuthenticatorWindow({ mode: "add" })}
        />
      );
    }
    if (win.id === SSO_AUTHENTICATOR_FORM_WINDOW_ID) {
      return (
        <AuthenticatorForm
          hideTitle
          presetAuthId={win.meta?.authId}
          presetClientId={win.meta?.clientId}
          onSaveSuccess={() => closeWindow(win.id)}
          onCancel={() => closeWindow(win.id)}
        />
      );
    }
    if (win.id === "systems") {
      return (
        <RunbookSystemGrid
          hideTitle
          onEditSystem={(system) => openSystemFormWindow({ mode: "edit", system })}
          onAddSystem={() => openSystemFormWindow({ mode: "add" })}
          onViewSystem={(system) => openSystemFormWindow({ mode: "view", system })}
        />
      );
    }
    if (win.id === SYSTEM_FORM_WINDOW_ID) {
      return (
        <RunbookSystemForm
          hideTitle
          readOnly={win.meta?.mode === "view"}
          presetSystem={win.meta?.system}
          onSaveSuccess={() => closeWindow(win.id)}
          onCancel={() => closeWindow(win.id)}
          onEditRequest={(system) => openSystemFormWindow({ mode: "edit", system })}
          onDeleted={() => closeWindow(win.id)}
        />
      );
    }
    if (win.id === "servers") {
      return (
        <RunbookServerGrid
          hideTitle
          onEditServer={(serverName) => openServerFormWindow({ mode: "edit", serverName })}
          onAddServer={() => openServerFormWindow({ mode: "add" })}
        />
      );
    }
    if (win.id === SERVER_FORM_WINDOW_ID) {
      return (
        <RunbookServerForm
          hideTitle
          presetServerName={win.meta?.serverName}
          onSaveSuccess={() => closeWindow(win.id)}
          onCancel={() => closeWindow(win.id)}
        />
      );
    }
    if (win.id === "mcp-tokens") {
      return <McpTokenGrid hideTitle />;
    }
    if (win.id === "azure-costs") {
      return <AzureCostGrid hideTitle />;
    }
    if (win.id === "usage-analytics") {
      return <UsageAnalyticsOverview user={user} hideTitle />;
    }
    if (win.id === "inspect-requests") {
      return <InspectRequestGrid hideTitle user={user} />;
    }
    if (win.id === "auditlog") {
      return <AuditLogGrid user={user} hideTitle />;
    }
    if (win.id === "my-analytics") {
      return <Analytics user={user} hideTitle />;
    }
    if (win.id === "all-clients-analytics") {
      return <AllClientsAnalytics user={user} hideTitle />;
    }
    if (win.id === "my-usage") {
      return <UsageAnalytics user={user} embedded />;
    }
    if (win.id === "invitations") {
      return <InvitationsGrid user={user} hideTitle />;
    }
    if (win.id === "permissions") {
      return <PermissionsManager user={user} hideTitle />;
    }
    if (win.id === "users") {
      return (
        <UsersGrid
          user={user}
          hideTitle
          refreshSignal={userListRefreshSignal}
          onEditUser={openUserFormWindow}
        />
      );
    }
    if (win.id === "runbook-tags") {
      return <RunbookTagGrid user={user} hideTitle />;
    }
    if (win.id === USER_FORM_WINDOW_ID) {
      return (
        <UserForm
          user={user}
          hideTitle
          presetUserId={win.meta?.userId}
          onSaveSuccess={() => {
            setUserListRefreshSignal((n) => n + 1);
            closeWindow(win.id);
          }}
          onCancel={() => closeWindow(win.id)}
        />
      );
    }
    if (win.id === "clients") {
      return (
        <ClientGrid
          user={user}
          hideTitle
          refreshSignal={clientListRefreshSignal}
          onRenewClient={(clientId) => openClientFormWindow({ mode: "renew", clientId })}
          onAddClient={() => openClientFormWindow({ mode: "add" })}
          onManageAuthenticators={openClientAuthenticatorsWindow}
        />
      );
    }
    if (win.id === CLIENT_FORM_WINDOW_ID) {
      return (
        <ClientForm
          hideTitle
          presetClientId={win.meta?.clientId}
          onSaveSuccess={() => {
            setClientListRefreshSignal((n) => n + 1);
            closeWindow(win.id);
          }}
          onCancel={() => closeWindow(win.id)}
        />
      );
    }
    if (win.id === CLIENT_AUTHENTICATORS_WINDOW_ID) {
      return (
        <AuthenticatorsGrid
          hideTitle
          presetClientId={win.meta?.clientId}
          onEditAuthenticator={(authId) =>
            openClientAuthenticatorFormWindow({ mode: "edit", authId, clientId: win.meta?.clientId })
          }
          onAddAuthenticator={(clientId) => openClientAuthenticatorFormWindow({ mode: "add", clientId })}
        />
      );
    }
    if (win.id === CLIENT_AUTHENTICATOR_FORM_WINDOW_ID) {
      return (
        <AuthenticatorForm
          hideTitle
          presetAuthId={win.meta?.authId}
          presetClientId={win.meta?.clientId}
          onSaveSuccess={() => closeWindow(win.id)}
          onCancel={() => closeWindow(win.id)}
        />
      );
    }
    if (win.id === "messages") {
      return (
        <BroadcastMessageGrid
          user={user}
          hideTitle
          onAddMessage={() => openMessageFormWindow(null)}
          onEditMessage={openMessageFormWindow}
          onViewReviews={openMessageReviewsWindow}
        />
      );
    }
    if (win.id === MESSAGE_FORM_WINDOW_ID) {
      return (
        <BroadcastMessageForm
          user={user}
          hideTitle
          presetMessageId={win.meta?.messageId}
          onSaveSuccess={() => closeWindow(win.id)}
          onCancel={() => closeWindow(win.id)}
        />
      );
    }
    if (win.id === MESSAGE_REVIEWS_WINDOW_ID) {
      return <BroadcastMessageReviewGrid user={user} hideTitle presetMessageId={win.meta?.messageId} />;
    }
    return null;
  };

  const handleOpen = (icon) => {
    if (icon.opensWindow) {
      openWindow(icon);
    } else {
      navigate(icon.to);
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        {!hideTitle && <Title text="Administration" icon={<AiOutlineSetting className="mb-1" />} />}
        {/* ms-auto: with the Title hidden (embedded case), this would otherwise be the only
            flex child, and a lone item under justify-content-between sits at the start, not
            the end — ms-auto pins it right regardless of whether the Title renders. */}
        <div className="d-none d-md-flex gap-2 ms-auto">
          {/* Only on the standalone /settings page — when Administration is embedded as a
              window inside the Workspace desktop (hideTitle), this is already redundant:
              you're looking at it from inside the Workspace. */}
          {!hideTitle && (user?.isSuperUser || user?.isProductTeam) && (
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => navigate("/workspace")}
              title="Back to the Workspace desktop"
            >
              <MdHome className="mb-1 me-1" />
              Workspace
            </button>
          )}
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={handleReset}
          >
            Reset Layout
          </button>
        </div>
      </div>

      <div className="settings-desktop d-none d-md-block" ref={containerRef} onClick={() => setSelectedId(null)}>
        {!hideTitle && (
          <img
            ref={watermarkRef}
            className="settings-desktop-watermark"
            src={darkMode ? armaninoLogoDark : armaninoLogo}
            alt=""
            aria-hidden="true"
          />
        )}
        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
          {visibleIcons.map((icon) => (
            <DesktopIcon
              key={icon.id}
              icon={icon}
              position={positions[icon.id]}
              isSelected={selectedId === icon.id}
              onSelect={setSelectedId}
              onOpen={handleOpen}
            />
          ))}
        </DndContext>
      </div>

      <DndContext sensors={windowSensors} onDragEnd={handleWindowDragEnd}>
        {openWindows.map((win) => (
          <DesktopWindow
            key={win.id}
            id={win.id}
            title={win.title}
            icon={<win.Icon />}
            accentColor={win.color}
            position={{ x: win.x, y: win.y }}
            size={{ width: win.width, height: win.height }}
            zIndex={WINDOW_Z_BASE + win.zIndex}
            minY={WINDOW_MIN_Y}
            isMinimized={win.isMinimized}
            isMaximized={win.isMaximized}
            onClose={closeWindow}
            onFocus={() => focusWindow(win.id)}
            onResize={handleWindowResize}
            onMinimize={minimizeWindow}
            onMaximizeToggle={toggleMaximizeWindow}
            onPopOut={win.to ? () => handlePopOutWindow(win) : undefined}
          >
            {renderWindowBody(win)}
          </DesktopWindow>
        ))}
      </DndContext>

      <div className="settings-desktop-mobile d-md-none">
        {visibleIcons.map(({ id, label, to, Icon, color }) => (
          <button
            key={id}
            type="button"
            className="settings-desktop-mobile-item"
            onClick={() => navigate(to)}
          >
            <span className="settings-desktop-icon-glyph" style={{ "--tile-color": color }}>
              <Icon />
            </span>
            <span className="settings-desktop-icon-label">{label}</span>
          </button>
        ))}
      </div>
    </>
  );
}
